import { ValueFormatter } from '@baseball-systems/formatting';
import { isNotNullish } from '@baseball-systems/utils';

export const HEAT_MAP_GRADIENT_BOUND = 0.75;

export const HITTER_BOX_SCORE_BUCKETS = [
  { val: 0, label: 'Process' },
  { val: 1, label: 'PA Log' },
  { val: 2, label: 'Pit Seen' },
  { val: 3, label: 'K-Zone' },
];

export const PITCHER_BOX_SCORE_BUCKETS = [
  { val: 0, label: 'Process' },
  { val: 1, label: 'Velo' },
  { val: 2, label: 'Grades' },
  { val: 3, label: 'Release' },
];

export const HITTER_BOX_SCORE_IDS = {
  0: ['pitches', 'xOPSBIP', 'exVelo', 'prodLA'],
  1: ['pa1', 'pa2', 'pa3', 'pa4', 'pa5', 'pa6'],
  2: ['ZonePct', 'HardPct', 'BreakPct', 'SoftPct', 'PctPAvL'],
  3: ['InZnSwPct', 'ChasePct', 'CorrectSwPct', 'ContactPct', 'InZnContactPct'],
};

export const PITCHER_BOX_SCORE_IDS = {
  0: ['FBSNVelo', 'FBSNUsePct', 'FBSNRiseHt', 'PctVsLHH'],
  1: ['FBVelo-Section', 'SNVelo', 'CBVelo', 'SLVelo', 'CHSPVelo', 'CTVelo'],
  2: ['FBGrade', 'SNGrade', 'CBGrade', 'SLGrade', 'CHSPGrade', 'CTGrade'],
  3: ['PitchReleaseZPoint', 'PitchHorizontalReleaseAnglePoint', 'PitchExtensionPoint', 'FBApproachAngle'],
};

export const CLE_ORG_OR_CLUB_ID = 1;

export const TICKER_GAME_TYPE_OPTIONS_DEFAULT = [
  { val: 'all', label: 'All', byDate: true },
  { val: 'game', label: 'Game', byDate: true },
  { val: 'non_game', label: 'Non-Game', byDate: true },
];

export const TICKER_GAME_TYPE_OPTIONS_LANDING_PAGE = [
  { val: 'all', label: 'By Date', byDate: true, gameOnly: false, allOrgs: true },
  { val: 'game', label: 'Last 10 ML', byDate: false, lastN: 10, nextN: 0, rank: 1, gameOnly: true },
  {
    val: 'game2',
    label: 'Next 10 ML',
    byDate: false,
    lastN: 1,
    nextN: 9,
    earliestDateFirst: true,
    rank: 1,
    gameOnly: true,
  },
];

export const TICKER_GAME_TYPE_OPTIONS_ORG = [
  { val: 'all', label: 'By Date', byDate: true, gameOnly: true },
  {
    val: 'game',
    label: 'Last 10 ML',
    byDate: false,
    lastN: 10,
    nextN: 0,
    rank: 1,
    gameOnly: true,
    defaultOption: true,
  },
  {
    val: 'game2',
    label: 'Next 10 ML',
    byDate: false,
    lastN: 1,
    nextN: 9,
    earliestDateFirst: true,
    rank: 1,
    gameOnly: true,
  },
];

export const TICKER_GAME_TYPE_OPTIONS_AFFILIATE = [
  {
    val: 'all',
    label: 'Recent/Upcoming',
    byDate: false,
    lastN: 4,
    nextN: 3,
    lastNBullpens: 3,
    earliestDateFirst: true,
  },
  { val: 'game', label: 'Last 10', byDate: false, lastN: 10, nextN: 0 },
  { val: 'game2', label: 'Next 10', byDate: false, lastN: 1, nextN: 9, earliestDateFirst: true },
  { val: 'non_game', label: 'Last 10 Bullpens', byDate: false, lastNBullpens: 10, bullpensOnly: true },
];

export const TICKER_GAME_TYPE_OPTIONS_PLAYER = [
  { val: 'all', label: "What's New", byDate: false },
  { val: 'game_only', label: 'Game', byDate: false },
  { val: 'game_bullpens', label: 'Non-Game', byDate: false },
];

export const REFERENCE_GAME_TYPE_OPTIONS = [
  { val: 0, label: 'Game' },
  { val: 1, label: 'Bullpen' },
  { val: 2, label: 'Game & Bullpen' },
];

export const PITCH_PROFILE_PBP_SPIN_OPTIONS = [
  { val: 'DirEff', label: 'Direction & Efficiency' },
  { val: 'DirAvg', label: 'Direction vs Your Average' },
];

export const PITCH_PROFILES_COMPONENT_OPTIONS = [
  { val: 'AllPitches', label: 'All Pitches' },
  { val: 'PitchByPitch', label: 'Pitch-by-Pitch' },
  { val: 'ReleasePoint', label: 'Release Point' },
];

export const PITCH_PROFILES_PROPERTY_OPTIONS = [
  { val: 'Velo', label: 'Avg Velo' },
  { val: 'FxStuffGrade', label: 'FX Stuff' },
  { val: 'FxCommandGrade', label: 'FX Command' },
];

export const HARD_PITCH_TYPE_OPTIONS = [
  { val: 'FB', label: 'FB' },
  { val: 'SN', label: 'SN' },
];

export const PITCH_PROFILES_RELEASE_VIEW_OPTIONS = [
  { val: 'Center', label: 'Center Field View' },
  { val: 'Side', label: '3B View' },
];

export const PITCH_PROFILES_SPIN_TRENDS_OPTIONS = [
  { val: 'SpinRate3D', label: 'Total Spin', colorDelta: 50, yAxisTickInterval: 100 },
  { val: 'spinEfficiency', label: 'Spin Efficiency', colorDelta: 5, yAxisTickInterval: 10 },
];

export const HANDED_OPTIONS = [
  { val: 0, label: 'All' },
  { val: 1, label: 'vs LHH' },
  { val: 2, label: 'vs RHH' },
];

export const COUNT_BUCKET_OPTIONS = [
  { val: 0, label: 'All', where: (x) => true, skyzoneCatId: 15 },
  { val: 1, label: '0-0', where: (x) => x.CtP1, skyzoneCatId: 1 },
  { val: 2, label: 'Mid', where: (x) => x.CtMid, skyzoneCatId: 21 },
  { val: 3, label: 'Hitter Adv', where: (x) => x.CtHitAdv, skyzoneCatId: 22 },
  { val: 4, label: '2 Strikes', where: (x) => x.Ct2K, skyzoneCatId: 23 },
  { val: 5, label: 'Full', where: (x) => x.CtFull, skyzoneCatId: 17 },
];

export const SPIN_PROPERTY_OPTIONS = [
  { val: 'DirEff', label: 'Direction & Efficiency' },
  { val: 'DirAvg', label: 'Direction vs Your Average' },
];

export const PERIOD_OPTIONS = [
  { val: 'All', label: 'All' },
  { val: 'Game', label: 'Game' },
  { val: 'Ref Period', label: 'Reference' },
  { val: 'Diff', label: 'Diff' },
];

export const ENABLE_WPA_GAME_TYPES = [1, 2, 3, 4, 5, 6];

export const COLOR_BY_OPTIONS = [
  { val: 'PitchColor', label: 'Pitch Type' },
  { val: 'PitchResult', label: 'Result' },
];

export const RELEASE_POINT_TABLE_PITCHTYPE_PROPS = [
  'PitchExtension',
  'PitchInitialPositionZ',
  'PitchInitialPositionX',
  'MeasuredArmSlot',
];

export const PITCH_PERFORMANCE_SUM_PROPERTIES = ['BIP'];

export const PITCH_PERFORMANCE_WEIGHTED_PROPERTIES = [
  'WeakContactPillar',
  'GBPct',
  'xHRPct',
  'xBABIP',
  'SoftHitPct',
  'GBPctPtl',
  'xHRPctPtl',
  'xBABIPPtl',
  'SoftHitPctPtl',
  'MissBatPillar',
  'SwKPct',
  'InZoneMissPct',
  'OutZoneMissPct',
  'HardMissPct',
  'SoftMissPct',
  'SwingKPctPtl',
  'InZoneMissPctPtl',
  'OutZoneMissPctPtl',
  'HardMissPctPtl',
  'SoftMissPctPtl',
  'GenerateStrikePillar',
  'KPct',
  'ZnPct',
  'ChasePct',
  'SwingPct',
  'ClKPct',
  'KPctPtl',
  'ZnPctPtl',
  'ChasePctPtl',
  'SwingPctPtl',
  'ClKPctPtl',
];

export const ALL_PITCHES_OPTIONS = [
  { val: 0, label: 'Pitcher Only' },
  { val: 1, label: 'All Pitches' },
];

export const OUTCOME_COUNT_BUCKET_PROPERTIES = [
  {
    val: 'CtP1',
    label: '0-0',
  },
  {
    val: 'CtMid',
    label: 'Mid Ct',
  },
  {
    val: 'CtHitAdv',
    label: 'Hit Adv',
  },
  {
    val: 'Ct2K',
    label: '2 Strikes',
  },
  {
    val: 'CtFull',
    label: 'Full Ct',
  },
];

export const CONTACT_QUALITY_MAP = {
  S: {
    label: 'S',
    val: 'S',
    color: 'rgb(0, 120, 200)',
  },
  M: {
    label: 'M',
    val: 'M',
    color: 'rgb(255, 255, 255)',
  },
  H: {
    label: 'H',
    val: 'H',
    color: 'rgb(240, 204, 204)',
  },
  C: {
    label: 'C',
    val: 'C',
    color: 'rgb(230, 100, 100)',
  },
};

export const SWING_OUTCOME_MAP = {
  StrikeSwinging: {
    abbr: 'Miss',
    color: 'rgb(190, 50, 50)',
    label: 'K (Swing)',
    val: 'StrikeSwinging',
  },
  StrikeLooking: {
    abbr: 'Take',
    label: 'K (Look)',
    color: 'rgb(235, 155, 155)',
    val: 'StrikeLooking',
  },
  Foul: {
    abbr: 'Foul',
    label: 'Foul',
    color: '#F2F2F2',
    val: 'Foul',
    hideLocationsAttack: true,
  },
  Ball: {
    abbr: 'Ball',
    color: 'rgb(10, 150, 100)',
    label: 'Ball',
    val: 'Ball',
  },
  InPlay: {
    abbr: 'BIP',
    label: 'In Play',
    color: 'rgb(50, 100, 205)',
    val: 'InPlay',
  },
  Pickoff: {
    abbr: 'PO',
    color: 'transparent',
    val: 'Pickoff',
  },
  Unknown: {
    abbr: '--',
    color: '#BFBFBF',
  },
  Unknown2: {
    abbr: '--',
    color: '#BFBFBF',
    val: 'Unknown2',
  },
};

export const OUTCOME_OPEN_KEYS = ['pitchTypeOpen', 'pitchResultOpen', 'countBucketOpen'];

export const SITUATION_ID_ROW_BORDERS = [0, 3, 4];

export const SITUATION_PT_PROPERTIES = [
  {
    type: 'FB',
  },
  {
    type: 'SN',
  },
  {
    type: 'HD',
    label: 'Hard',
    dblBorder: true,
  },
  {
    type: 'CB',
  },
  {
    type: 'SL',
  },
  {
    type: 'CH',
  },
  {
    type: 'CT',
  },
  {
    type: 'SP',
  },
];

export const VISIBLE_TICKER_GAME_STATUS_IDS = [1, 2, 3, 4, 5, 6];

export const HITTER_PA_COLS = [
  {
    headerName: '#',
    field: 'PaPitchNum',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 25,
  },
  {
    headerName: 'Count',
    field: 'CountText',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 35,
  },
  {
    headerName: 'Type',
    field: 'PitchType',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 30,
    cellStyle: (params) => {
      return {
        background: params.data.PitchColor,
        color: 'white',
      };
    },
  },
  {
    headerName: 'Result',
    field: 'SwingResultText',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    cellClass: ['string'],
    headerClass: ['string'],
    width: 55,
  },
];

export const HITTER_PITCH_TYPE_PCT_COLS = [
  {
    headerName: 'FB',
    field: 'FBPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'SN',
    field: 'SNPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'Hard',
    field: 'HardPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral', 'left-thin-divider'],
    headerClass: ['numeral', 'left-thin-divider'],
    width: 30,
  },
  {
    headerName: 'CB',
    field: 'CBPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral', 'left-thin-divider'],
    headerClass: ['numeral', 'left-thin-divider'],
    width: 30,
  },
  {
    headerName: 'SL',
    field: 'SLPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'CH',
    field: 'CHPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'CT',
    field: 'CTPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'SW',
    field: 'SWPct',
    valueFormatter: 'zeroesEmptify | multiply(100) | round | percent',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
];

export const RESULT_COLS = [
  {
    headerName: 'H',
    field: 'H',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: 'AB',
    field: 'AB',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: 'K',
    field: 'K',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: 'BB',
    field: 'BB',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: '2B',
    field: 'B2',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: '3B',
    field: 'B3',
    valueFormatter: 'emptify',
    width: 25,
  },
  {
    headerName: 'HR',
    field: 'HR',
    valueFormatter: 'emptify',
    width: 25,
  },
];

export const BATTER_1P_COLS = [
  {
    headerName: '1P Swing',
    cellRenderer: (params) => {
      const p1StrikePct = new ValueFormatter(params.row.P1Strike / params.row.P1).multiply(100).round(0).percent();
      return `${params.row.P1Strike}/${params.row.P1} (${p1StrikePct})`;
    },
    width: 100,
  },
];

export const BATTER_PITCH_MIX_COLS = [
  {
    headerName: 'HR',
    field: 'HR',
    valueFormatter: 'emptify',
    width: 25,
  },
];

const ifDiffAddSign = (params, valueFormatter) => {
  return params.data.What === 'Diff' ? valueFormatter.prependSign() : valueFormatter;
};

export const PITCH_TYPE_AVERAGES_COLS = [
  {
    headerName: 'PT',
    field: 'PitchType',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 30,
    cellStyle: (params) => {
      return {
        background: params.data.PitchColor,
        color: 'white',
      };
    },
  },
  {
    headerName: 'Period',
    field: 'What',
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 60,
  },
  {
    headerName: 'NP',
    field: 'NP',
    valueGetter: (params) => {
      return params.data.What === 'Diff' ? '--' : params.data.NP;
    },
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 40,
  },
  {
    headerName: 'Min Velo',
    field: 'MinVelo',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral', 'no-left-divider'],
    cellClass: ['numeral', 'no-left-divider'],
    width: 40,
  },
  {
    headerName: 'Max Velo',
    field: 'MaxVelo',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 40,
  },
  {
    headerName: 'Velo',
    field: 'Velo',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Cut',
    field: 'Cut',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Vert Mvmt',
    field: 'Rise',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 45,
  },
  {
    headerName: 'Spin Dir',
    field: 'SpinAxisClock',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Spin Effic.',
    field: 'SpinEfficiency',
    valueFormatter: (params) =>
      ifDiffAddSign(params, new ValueFormatter(params.value).multiply(100).round(0).emptify()).percent(),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Useful Spin',
    field: 'SpinRate2D',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(0).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Total Spin',
    field: 'SpinRate3D',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(0).emptify()),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 40,
  },
  {
    headerName: 'fxStuff',
    field: 'FxStuffGrade',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'fxCmd',
    field: 'FxCommandGrade',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'fxPit',
    field: 'FxPitchGrade',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 40,
  },
  {
    headerName: 'Height',
    field: 'PitchInitialPositionZ',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, params.data.What === 'Diff'),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Side',
    field: 'PitchInitialPositionX',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, params.data.What === 'Diff'),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Ext',
    field: 'PitchExtension',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, params.data.What === 'Diff'),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 40,
  },
  {
    headerName: 'Strike %',
    field: 'StrikePct',
    valueFormatter: (params) =>
      ifDiffAddSign(params, new ValueFormatter(params.value).multiply(100).round(0).emptify()).percent(),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 45,
  },
  {
    headerName: 'Zone %',
    field: 'ZonePct',
    valueFormatter: (params) =>
      ifDiffAddSign(params, new ValueFormatter(params.value).multiply(100).round(0).emptify()).percent(),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 45,
  },
  {
    headerName: 'Swing %',
    field: 'SwingPct',
    valueFormatter: (params) =>
      ifDiffAddSign(params, new ValueFormatter(params.value).multiply(100).round(0).emptify()).percent(),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 45,
  },
  {
    headerName: 'Miss %',
    field: 'MissPct',
    valueFormatter: (params) =>
      ifDiffAddSign(params, new ValueFormatter(params.value).multiply(100).round(0).emptify()).percent(),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 45,
  },
  {
    headerName: 'Exit Velo',
    field: 'HitExitSpeed',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Launch Angle',
    field: 'HitLaunchAngle',
    valueFormatter: (params) => ifDiffAddSign(params, new ValueFormatter(params.value).round(1).emptify()),
    headerClass: ['numeral'],
    cellClass: ['numeral'],
    width: 40,
  },
];

export const ALL_PITCHES_COLS = [
  {
    headerName: 'Inn',
    field: 'Inning',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerClass: ['gray-header', 'right-divider', 'inning-header'],
    cellClass: ['right-divider'],
    width: 45,
  },
  {
    headerName: 'Pitch #',
    field: 'PitchNumDisplay',
    filter: 'agNumberColumnFilter',
    headerClass: ['gray-header', 'right-divider', 'numeral'],
    cellClass: ['right-divider', 'numeral'],
    width: 41,
  },
  {
    headerName: 'Type',
    field: 'PitchType',
    headerClass: ['right-divider', 'gray-header'],
    cellClass: ['right-divider'],
    width: 33,
    cellStyle: (params) => {
      return {
        background: params.data.PitchColor,
        color: 'white',
      };
    },
  },
  {
    headerName: 'Video',
    headerClass: ['gray-header', 'right-divider'],
    cellClass: ['right-divider'],
    width: 40,
    cellRendererFramework: 'WatchPlayRenderer',
  },
  {
    headerName: 'Pitcher',
    field: 'PitcherName',
    headerClass: ['right-divider', 'gray-header', 'string'],
    cellClass: ['right-divider', 'string'],
    width: 60,
  },
  {
    field: 'PitchVelocity',
    headerName: 'Velo',
    filter: 'agNumberColumnFilter',
    width: 32,
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    valueFormatter: (params) => new ValueFormatter(params.value).round(1),
  },
  {
    field: 'PitchCut',
    headerName: 'Cut',
    filter: 'agNumberColumnFilter',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 40,
    valueFormatter: (params) => new ValueFormatter(params.value).round(1).inches(),
  },
  {
    field: 'PitchRise',
    headerName: 'Vert Mvmt',
    headerClass: ['right-divider', 'numeral'],
    cellClass: ['right-divider', 'numeral'],
    filter: 'agNumberColumnFilter',
    width: 37,
    valueFormatter: (params) => new ValueFormatter(params.value).round(1).inches(),
  },
  {
    headerName: 'Spin Dir',
    field: 'PitchSpinAxisClock',
    filter: 'agTextColumnFilter',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 38,
    valueFormatter: (params) => new ValueFormatter(params.value).round(0),
  },
  {
    headerName: 'Spin Eff',
    field: 'SpinEfficiency',
    filter: 'agNumberColumnFilter',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 31,
    valueFormatter: (params) => new ValueFormatter(params.value).multiply(100).round(0).percent(),
  },
  {
    headerName: 'Useful Spin',
    field: 'PitchSpinRate2D',
    filter: 'agNumberColumnFilter',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 34,
    valueFormatter: (params) => new ValueFormatter(params.value).emptify().round(0).formatNum('0,0'),
  },
  {
    headerName: 'Total Spin',
    field: 'PitchSpinRate3D',
    filter: 'agNumberColumnFilter',
    headerClass: ['right-divider', 'numeral'],
    cellClass: ['right-divider', 'numeral'],
    width: 35,
    valueFormatter: (params) => new ValueFormatter(params.value).emptify().round(0).formatNum('0,0'),
  },
  {
    headerName: 'Height',
    field: 'PitchInitialPositionZ',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, false),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'Side',
    field: 'PitchInitialPositionX',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, false),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 30,
  },
  {
    headerName: 'Ext',
    field: 'PitchExtension',
    valueFormatter: (params) => new ValueFormatter(params.value).decToFeetInches(0, false, false),
    headerClass: ['numeral', 'right-divider'],
    cellClass: ['numeral', 'right-divider'],
    width: 35,
  },
  {
    headerName: 'Result',
    field: 'SwingResultDescription',
    headerClass: ['string'],
    cellClass: ['string'],
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
    width: 55,
  },
  {
    headerName: 'Pitch Loc',
    cellClass: ['svg-img'],
    width: 45,
    cellRendererFramework: 'PitchLocRenderer',
  },
  {
    headerName: 'Hit Loc',
    width: 40,
    cellClass: ['hit-loc-svg'],
    cellRendererFramework: 'HitLocRenderer',
  },
  {
    field: 'ExitVelocity',
    headerName: 'Exit Velo',
    filter: 'agNumberColumnFilter',
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 37,
    valueFormatter: (params) => new ValueFormatter(params.value).round(1),
  },
  {
    field: 'LaunchAngle',
    headerName: 'Launch Ang',
    cellClass: ['right-divider', 'numeral'],
    headerClass: ['right-divider', 'numeral'],
    filter: 'agNumberColumnFilter',
    width: 42,
    valueFormatter: (params) => new ValueFormatter(params.value).round(1),
  },
  {
    headerName: 'Hit Team',
    field: 'HittingTeamAbbr',
    width: 35,
    filter: 'agTextColumnFilter',
    valueFormatter: (params) => {
      if (params.value && params.value.includes('MnST')) {
        return params.value.split('MnST')[0];
      }

      return params.value;
    },
  },
  {
    headerName: 'Hitter',
    field: 'BatterName',
    headerClass: ['string'],
    cellClass: ['string'],
    width: 84,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'R/L',
    field: 'BatHandedId',
    filter: 'agTextColumnFilter',
    width: 23,
    valueFormatter: (params) => {
      return params.value === 1 ? 'L' : 'R';
    },
  },
  {
    headerName: 'Count',
    filter: 'agTextColumnFilter',
    width: 37,
    valueGetter: (params) => {
      const row = params.data;
      if (isNotNullish(row.Strikes) && isNotNullish(row.Balls)) {
        return `${row.Balls}-${row.Strikes}`;
      }
      return '--';
    },
  },
  {
    headerName: 'Outs',
    field: 'Outs',
    filter: 'agNumberColumnFilter',
    width: 47,
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    valueFormatter: (params) => new ValueFormatter(params.value).emptify(),
  },
  {
    headerName: 'On Base',
    width: 37,
    cellClass: ['right-divider'],
    headerClass: ['right-divider'],
    filter: 'agNumberColumnFilter',
    cellRendererFramework: 'OutStateRenderer',
  },
  {
    headerName: 'xOPS',
    field: 'xOPS',
    valueFormatter: (params) => new ValueFormatter(params.value).divide(1000).round(3, false),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 43,
  },
  {
    headerName: 'xOPS Gain',
    field: 'xOPSDiff',
    valueFormatter: (params) => new ValueFormatter(params.value).divide(1000).round(3, false).prependSign(),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 40,
  },
  {
    headerName: 'xRV Stuff',
    field: 'stfRVLWPit',
    valueFormatter: (params) => new ValueFormatter(params.value).round(2),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 35,
  },
  {
    headerName: 'xRV w/Loc',
    field: 'xRVpitchloc',
    valueFormatter: (params) => new ValueFormatter(params.value).round(2),
    cellClass: ['numeral'],
    headerClass: ['numeral'],
    width: 38,
  },
];
